import withRouter from "Components/Common/withRouter";
import RocksFields from "Components/organisms/forms/rocks-form/types";
import { FormikHelpers, useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { HumanResourcesSliceState } from "slices/human-resources/reducer";
import { RocksSliceState } from "slices/business-development/rocks/reducer";
import { editRocksForm, getRock, getRocksSearchFields, submitRocksForm } from "slices/business-development/rocks/thunk";
import { getManagers } from "slices/human-resources/thunk";
import RocksFormTemplate from "../../../../Components/templates/business-development/rocks/rocks-form";
import { QUARTERS, RocksFormValidation, YEARS } from "data/rocks-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Rocks } from "models/rocks";



const selectRocks = createSelector(
    (state: any) => state.Rocks,
    ({ rocksStatus }: RocksSliceState) => ({
        rocksStatus
    })
)

const selectHumanResources = createSelector(
    (state: any) => state.HumanResources,
    ({ managers }: HumanResourcesSliceState) => ({
        managers
    })
)


const EditRocksPage = (props: any) => {
    document.title = "Edit Rock | IbexIQ";
    const [isLoadingRock, setIsLoadingRock] = useState<boolean>(true);
    const [rock, setRock] = useState<Rocks | null>(null);
    const dispatch: any = useDispatch();
    const [searchParams] = useSearchParams();

    const {
        managers
    } = useSelector(selectHumanResources);

    const {
        rocksStatus,
    } = useSelector(selectRocks);


    const initializePage = async (rockId: string) => {
        if (rocksStatus.length === 0 || managers.length === 0) {
            await dispatch(getManagers())
            await dispatch(getRocksSearchFields())
        }


        const rock = await getRock(rockId);

        if (!rock) {
            props.router.navigate(-1)
        }

        setRock(rock);
        setIsLoadingRock(false);
    }


    useEffect(() => {
        const rockId = searchParams.get("rockId");

        if (!rockId) {
            toast.error("Something went wrong. Rock Id not found")
            props.router.navigate(-1)
        } else {
            initializePage(rockId)
        }
    }, [])

    const handleSubmitRocksForm = async (vals: RocksFields, helpers: FormikHelpers<RocksFields>) => {
        const rockId = searchParams.get("rockId");

        if (!rockId) {
            toast.error("Something went wrong. Rock Id not found")
            props.router.navigate(-1)
            return;
        }
        helpers.setSubmitting(true);

        const res = await dispatch(editRocksForm(vals, rockId, () => helpers.resetForm({})))

        if (res)
            handleBack();
        helpers.setSubmitting(false);
    }

    const initialValues: RocksFields = useMemo(() => ({
        accountableID: rock?.accountableID ?? "",
        statusId: rock?.statusId ?? "",
        quarter: rock?.quarter ?? "",
        year: rock?.year ?? "",
        helpFrom: rock?.helpFrom ?? "",
        completedAt: rock?.completedAt?.toString().split("T")[0] ?? "",
        description: rock?.description ?? "",
        rocks: rock?.rocks ?? "",
    }), [rock])

    const { values, errors, touched, handleSubmit, handleBlur, handleChange, isValid, isSubmitting } = useFormik({
        onSubmit: handleSubmitRocksForm,
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: RocksFormValidation
    })

    const handleBack = () => {
        props.router.navigate(-1);
    }

    return <RocksFormTemplate
        values={values}
        years={[...YEARS.map(y => ({ label: String(y), value: String(y) }))]}
        quarters={[...QUARTERS.map(q => ({ label: q, value: q }))]}
        employees={[...managers.map(e => ({ label: `${e.commonName ?? e.firstName} ${e.lastName}`, value: e.id }))]}
        status={[...rocksStatus.map(r => ({ label: r.description, value: r.id }))]}
        breadcrumbItem={"Create Rock"}
        title={"Create Rock"}
        subtitle={"Please fill out the following fields and click submit."}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleSubmit={handleSubmit}
        handleCancel={handleBack}
        isValid={isValid}
        isSubmitting={isSubmitting}
        isLoadingRock={isLoadingRock}
        edit={true}
    />

}

export default withRouter(EditRocksPage);